/**
 * base_widgets.js — Shared Algolia InstantSearch v4 widget infrastructure.
 *
 * Provides helpers and factory functions used by all search pages.
 * Import from this module instead of re-implementing common widgets.
 */
import $ from 'jquery';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Wrap a custom widget config to guarantee v4 lifecycle compliance.
 * Ensures `render`, `init`, and `dispose` always exist.
 */
export function createCustomWidget({ init, render, dispose } = {}) {
    return {
        init: init || function () { },
        render: render || function () { },
        dispose: dispose || function () { }
    };
}

/**
 * Safely add widgets to an InstantSearch instance.
 *
 * IS v4 throws fatal "Container must be string or HTMLElement" errors at
 * widget CREATION time. Pass widget factories as zero-arg arrow functions
 * and they'll be wrapped in try-catch. Already-created widget objects are
 * added directly.
 *
 * @param {Object} searchInstance - The instantsearch instance
 * @param {Array<Function|Object>} entries - Mix of `() => widget()` factories
 *   and pre-created widget objects (like `configure()`)
 */
export function safeAddWidgets(searchInstance, entries) {
    const widgets = [];
    for (const entry of entries) {
        if (entry == null) continue;

        // Arrow factory callbacks: typeof function, no widget lifecycle methods
        if (typeof entry === 'function' && !entry.$$type && !entry.init && !entry.render) {
            try {
                const w = entry();
                if (w != null) widgets.push(w);
            } catch (e) {
                if (e.message && e.message.indexOf('Container') > -1) {
                    // Expected when the DOM element doesn't exist (e.g. restricted view)
                } else {
                    throw e;
                }
            }
        } else {
            widgets.push(entry);
        }
    }
    if (widgets.length > 0) {
        searchInstance.addWidgets(widgets);
    }
}

/**
 * Safely extract helper state from v4 render options.
 * v4 custom widgets do NOT receive `renderOptions.state` — use this instead.
 */
export function getHelperState(renderOptions) {
    const helper =
        renderOptions.instantSearchInstance &&
        renderOptions.instantSearchInstance.helper;
    return helper ? helper.state : {};
}

/**
 * Escape a string for safe use inside an HTML attribute.
 */
export function escapeAttr(str) {
    if (str == null) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// ---------------------------------------------------------------------------
// Shared Templates
// ---------------------------------------------------------------------------

/**
 * Standard refinement list item template with XSS-safe attribute quoting.
 */
export function refinementListTemplate(hit) {
    const count_html = hit.count === 0
        ? ''
        : `<span class='ais-RefinementList-count'>${hit.count}</span>`;
    return `<label class='ais-RefinementList-label'><input type='checkbox' class='ais-RefinementList-checkbox' ${hit.isRefined ? 'checked' : ''} value="${escapeAttr(hit.value)}"><span class='ais-RefinementList-labelText'>${hit.highlighted}</span></input>${count_html}</label>`;
}

// ---------------------------------------------------------------------------
// Shared Widget Factories
// ---------------------------------------------------------------------------

/**
 * Stats widget — displays "N results found in Xms".
 */
export function createStatsWidget(container = '#number-of-results') {
    return instantsearch.widgets.stats({ container });
}

/**
 * Search box widget — keyword input with hidden submit/reset buttons.
 */
export function createSearchBoxWidget({ container = '#search-box', placeholder = '' } = {}) {
    return instantsearch.widgets.searchBox({
        container,
        placeholder,
        cssClasses: {
            submit: ['hidden'],
            reset: ['hidden']
        }
    });
}

/**
 * Clear refinements widget — "Clear Filters" button.
 */
export function createClearAllWidget({
    container = '#clear-filters',
    label = 'Clear Filters',
    excludedAttributes = [],
    clearsQuery = false
} = {}) {
    return instantsearch.widgets.clearRefinements({
        container,
        templates: { resetLabel: label },
        cssClasses: {
            button: 'button tertiary small fa-undo ais-clear-all--link'
        },
        excludedAttributes,
        clearsQuery
    });
}

/**
 * Save search widget — shows a save button when filters are active.
 * Uses getHelperState() for v4-safe state access.
 */
export function createSaveSearchWidget({
    container = '#save-search',
    buttonTag = 'button',
    buttonClass = 'button tertiary small fa-bookmark'
} = {}) {
    return createCustomWidget({
        render(renderOptions) {
            $(container).empty();
            const state = getHelperState(renderOptions);
            if (
                (_.keys(state.disjunctiveFacetsRefinements || {}).length !== 0) ||
                state.query
            ) {
                $(container).append(
                    `<${buttonTag} class='${buttonClass}'>${I18n.t('v4.saved_searches.save_search')}</${buttonTag}>`
                );
            }
        }
    });
}

/**
 * Current refined values widget — displays active filter chips.
 * Guards against WorkplaceUtils not being available on public pages.
 * Uses event delegation to prevent listener leaks.
 */
export function createCurrentRefinedWidget() {
    const renderMethod = (renderOptions, isFirstRender) => {
        if (isFirstRender) return;
        const { items } = renderOptions;
        const container = document.querySelector('#current-refined-values');
        if (!container) return;

        if (typeof WorkplaceUtils === 'undefined' || typeof WorkplaceUtils.currentRefinedTemplate !== 'function') {
            container.innerHTML = '';
            return;
        }

        container.innerHTML = `<div class='ais-root ais-CurrentRefinements'>
      <div class='ais-body ais-CurrentRefinements-body'>
        <div class='ais-CurrentRefinements-list'>
          ${_.map(items, item => WorkplaceUtils.currentRefinedTemplate(item)).join('')}
        </div>
      </div>
    </div>`;

        $('#current-refined-values')
            .off('click', '.ais-CurrentRefinements-link')
            .on('click', '.ais-CurrentRefinements-link', e => {
                renderOptions.refine(e.target.dataset);
            });
    };

    return instantsearch.connectors.connectCurrentRefinements(renderMethod);
}

/**
 * Description (search within descriptions) toggle widget.
 * Adds/removes a description attribute from searchable attributes on checkbox click.
 */
export function createDescriptionWidget(element, attributes, descriptionAttribute) {
    return createCustomWidget({
        init(options) {
            if (!element) return;
            $(element).on('click', function () {
                const attrs = [...attributes];
                if ($(this).is(':checked')) { attrs.push(descriptionAttribute); }
                options.helper.setQueryParameter('restrictSearchableAttributes', attrs);
                options.helper.search();
            });
        }
    });
}

/**
 * Quick list widget — handles smart list click navigation.
 */
export function createQuickListWidget(selector) {
    return createCustomWidget({
        init(options) {
            $(document).on('click', `${selector} li:not(.saved_search) a`, () =>
                options.helper.setQuery('').search()
            );
        }
    });
}

/**
 * Pagination widget — standard page navigation.
 */
export function createPaginationWidget(container = '#pagination-result') {
    return instantsearch.widgets.pagination({
        container,
        cssClasses: {
            root: 'pagination top-buffer',
            active: 'current'
        }
    });
}

/**
 * Date period widget — date range filter using datepicker.
 * Replaces WidgetFactories.dateWidget with v4-compatible implementation
 * that includes a render() stub.
 */
export function createDatePeriodWidget(fromSelector, toSelector) {
    const DAY_IN_SECONDS = 60 * 60 * 24;

    return instantsearch.connectors.connectRange(
        function (options, isFirstRendering) {
            if (!isFirstRendering) return;

            const { refine, start } = options;

            $(document).on('click', '#clear-filters', function () {
                $(fromSelector).datepicker('setDate', null);
                $(toSelector).datepicker('setDate', null);
            });

            $(document).ready(function () {
                if (start[0] && (start[0] !== -Infinity)) {
                    $(fromSelector).datepicker('setDate', new Date(start[0] * 1000));
                }
                if (start[1] && (start[1] !== Infinity) && (start[1] < 2147483647)) {
                    $(toSelector).datepicker('setDate', new Date(start[1] * 1000));
                }
            });

            $(document).on('change', `${fromSelector}, ${toSelector}`, function () {
                const fromDate = $(fromSelector).datepicker('getDate');
                const toDate = $(toSelector).datepicker('getDate');
                const fromTime = fromDate ? Math.floor(fromDate.getTime() / 1000) : 0;

                if (fromTime === 0 && !toDate) return;

                const toTime = toDate
                    ? Math.floor(toDate.getTime() / 1000) + DAY_IN_SECONDS - 1
                    : 2147483647;

                refine([fromTime, toTime]);
            });
        }
    );
}
